import React, { useState } from 'react';
import { CheckCircle, Clock, AlertCircle, XCircle, TrendingUp, Edit2, Save, X, FileEdit  } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';

const MilestoneStatusCard = ({ phaseSummary, onUpdate, onEditInvoice  }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedStatus, setEditedStatus] = useState(phaseSummary?.milestoneStatus || 'not_started');
  const [editedProgress, setEditedProgress] = useState(phaseSummary?.completionPercentage || 0);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'delayed':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'blocked':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'delayed':
        return 'bg-orange-100 text-orange-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Terminé';
      case 'in_progress':
        return 'En cours';
      case 'delayed':
        return 'Retardé';
      case 'blocked':
        return 'Bloqué';
      case 'not_started':
        return 'Non démarré';
      default:
        return status;
    }
  };

  const toNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const handleSave = async () => {
    if (!phaseSummary?.phaseId) {
      toast.error('ID de phase manquant');
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        milestone_status: editedStatus,
        milestone_completion_percentage: parseInt(editedProgress),
        updated_at: new Date().toISOString(),
      };

      // If status is completed, set completion date
      if (editedStatus === 'completed' && phaseSummary?.milestoneStatus !== 'completed') {
        updateData.milestone_completion_date = new Date().toISOString();
      }

      const { error } = await supabase
        .from('payment_phases')
        .update(updateData)
        .eq('id', phaseSummary.phaseId);

      if (error) throw error;

      toast.success('Statut mis à jour avec succès!');
      setIsEditing(false);
      
      // Callback to refresh data in parent component
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Error updating milestone status:', error);
      toast.error('Erreur lors de la mise à jour: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedStatus(phaseSummary?.milestoneStatus || 'not_started');
    setEditedProgress(phaseSummary?.completionPercentage || 0);
    setIsEditing(false);
  };

  const completionPercentage = toNumber(isEditing ? editedProgress : phaseSummary?.completionPercentage);
  const totalMilestones = toNumber(phaseSummary?.totalMilestones);
  const completedMilestones = toNumber(phaseSummary?.completedMilestones);
  const inProgressMilestones = toNumber(phaseSummary?.inProgressMilestones);
  const delayedMilestones = toNumber(phaseSummary?.delayedMilestones);

  const currentStatus = isEditing ? editedStatus : phaseSummary?.milestoneStatus;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1">
          {getStatusIcon(currentStatus)}
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">
              Phase {phaseSummary?.phaseNumber}: {phaseSummary?.phaseName}
            </h3>
            
            {isEditing ? (
              <select
                value={editedStatus}
                onChange={(e) => setEditedStatus(e.target.value)}
                className="mt-1 px-2 py-1 rounded border border-gray-300 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="not_started">Non démarré</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
                <option value="delayed">Retardé</option>
                <option value="blocked">Bloqué</option>
              </select>
            ) : (
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusColor(currentStatus)}`}>
                {getStatusLabel(currentStatus)}
              </span>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {completionPercentage}%
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              Progression
            </div>
          </div>
          
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Modifier"
            >
              <Edit2 className="w-4 h-4 text-gray-600" />
            </button>
          ) : (
            <div className="flex gap-1">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50"
                title="Enregistrer"
              >
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="p-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors disabled:opacity-50"
                title="Annuler"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar with Editor */}
      <div className="mb-3">
        {isEditing && (
          <div className="flex items-center gap-2 mb-2">
            <input
              type="range"
              min="0"
              max="100"
              value={editedProgress}
              onChange={(e) => setEditedProgress(e.target.value)}
              className="flex-1"
            />
            <input
              type="number"
              min="0"
              max="100"
              value={editedProgress}
              onChange={(e) => {
                const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                setEditedProgress(val);
              }}
              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <span className="text-sm text-gray-600">%</span>
          </div>
        )}
        
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${
              currentStatus === 'completed'
                ? 'bg-green-500'
                : currentStatus === 'in_progress' ? 'bg-blue-500'
                : currentStatus === 'delayed' ? 'bg-orange-500' : 'bg-gray-400'
            }`}
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Milestone Stats */}

      
     
    </div>
  );
};

export default MilestoneStatusCard;